import React, { useState, useRef, useEffect } from "react";
import downArrowSvg from "assets/img/arrow-down.svg";
import CustomCheckbox from "./CustomCheckbox";

interface HeaderData {
  header: string;
  values: string[];
}

interface TypeData {
  type: string;
  data: HeaderData[];
}

interface ItemSelectProps {
  value: string | string[];
  data: TypeData[];
  onChange: (val: string | string[]) => void;
  multiSelect?: boolean;
  isDisabled?: boolean;
  hideHeader?: boolean;
}

const ItemSelect: React.FC<ItemSelectProps> = ({
                                                 value,
                                                 data,
                                                 onChange,
                                                 multiSelect = false,
                                                 isDisabled = false,
                                                 hideHeader = false,
                                               }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedHeaders, setExpandedHeaders] = useState<Set<string>>(new Set());
  const [selectedValues, setSelectedValues] = useState<Set<string>>(
    new Set(Array.isArray(value) ? value : value ? [value] : [])
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedValues(new Set(Array.isArray(value) ? value : value ? [value] : []));
  }, [value]);

  const toggleHeader = (header: string) => {
    const newExpanded = new Set(expandedHeaders);
    newExpanded.has(header) ? newExpanded.delete(header) : newExpanded.add(header);
    setExpandedHeaders(newExpanded);
  };

  const handleValueSelect = (val: string) => {
    if (multiSelect) {
      const newSelected = new Set(selectedValues);
      newSelected.has(val) ? newSelected.delete(val) : newSelected.add(val);
      setSelectedValues(newSelected);
      onChange(Array.from(newSelected));
    } else {
      setSelectedValues(new Set([val]));
      onChange(val);
      setIsOpen(false);
    }
  };

  const handleSelectAllGlobal = () => {
    const allValues = data.flatMap((type) => type.data.flatMap((d) => d.values));
    const newSelected = new Set(selectedValues);
    const allSelected = allValues.every((v) => newSelected.has(v));

    if (allSelected) {
      allValues.forEach((v) => newSelected.delete(v));
    } else {
      allValues.forEach((v) => newSelected.add(v));
    }

    setSelectedValues(newSelected);
    onChange(Array.from(newSelected));
  };

  const handleHeaderSelect = (header: string) => {
    const headerData = data.flatMap((type) => type.data).find((d) => d.header === header);
    if (!headerData) return;

    const headerValues = headerData.values;
    const allSelected = headerValues.every((v) => selectedValues.has(v));

    const newSelected = new Set(selectedValues);
    if (allSelected) {
      headerValues.forEach((v) => newSelected.delete(v));
    } else {
      headerValues.forEach((v) => newSelected.add(v));
    }

    setSelectedValues(newSelected);
    onChange(Array.from(newSelected));
  };

  const isHeaderSelected = (header: string) => {
    const headerData = data.flatMap((type) => type.data).find((d) => d.header === header);
    if (!headerData) return false;
    return headerData.values.every((v) => selectedValues.has(v));
  };

  const isHeaderPartiallySelected = (header: string) => {
    const headerData = data.flatMap((type) => type.data).find((d) => d.header === header);
    if (!headerData) return false;
    const selectedCount = headerData.values.filter((v) => selectedValues.has(v)).length;
    return selectedCount > 0 && selectedCount < headerData.values.length;
  };

  const getDisplayText = () => {
    if (isDisabled || selectedValues.size === 0) return "Select Item";
    if (selectedValues.size === 1) return Array.from(selectedValues)[0];
    return `${selectedValues.size} Items Selected`;
  };

  const filteredData = searchTerm
    ? data.map((type) => ({
      ...type,
      data: type.data.filter(
        (d) =>
          d.header.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.values.some((v) => v.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    }))
    : data;

  const allValues = data.flatMap((type) => type.data.flatMap((d) => d.values));
  const isAllSelected = allValues.length > 0 && allValues.every((v) => selectedValues.has(v));

  return (
    <div style={styles.inputGroup}>
      <div style={styles.selectWrapper} ref={dropdownRef}>
        <div
          style={{
            ...styles.selectInput,
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
        >
          <span style={styles.selectText} title={getDisplayText()}>
            {getDisplayText()}
          </span>
          <img src={downArrowSvg} alt="" style={styles.selectArrow} />
        </div>

        {isOpen && !isDisabled && (
          <div style={styles.dropdown}>
            <div style={styles.searchWrapper}>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div style={styles.optionsList}>
              {multiSelect && (
                <div style={styles.option}>
                  <CustomCheckbox
                    checked={isAllSelected}
                    onChange={handleSelectAllGlobal}
                    label="Select All"
                  />
                </div>
              )}

              {filteredData.map((type) =>
                type.data.map((item) => (
                  <div key={item.header}>
                    <div style={styles.stateOption} onClick={() => toggleHeader(item.header)}>
                      <div
                        style={{
                          ...styles.chevron,
                          transform: expandedHeaders.has(item.header) ? "rotate(0deg)" : "rotate(-90deg)",
                          mask: `url(${downArrowSvg}) no-repeat center / contain`,
                          WebkitMask: `url(${downArrowSvg}) no-repeat center / contain`,
                          backgroundColor: "#E5B42F",
                          width: 12,
                          height: 12,
                          display: "inline-block",
                        }}
                      />
                      {multiSelect && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <CustomCheckbox
                            checked={isHeaderSelected(item.header)}
                            indeterminate={isHeaderPartiallySelected(item.header)}
                            onChange={() => handleHeaderSelect(item.header)}
                            label={item.header}
                          />
                        </div>
                      )}
                      {!multiSelect && (
                        <span style={{ ...styles.optionText, fontWeight: 500 }} title={item.header}>
                          {item.header}
                        </span>
                      )}
                    </div>

                    {expandedHeaders.has(item.header) && (
                      <div style={styles.storesList}>
                        {item.values
                          .filter((v) => !searchTerm || v.toLowerCase().includes(searchTerm.toLowerCase()))
                          .map((val) => (
                            <div key={val} style={styles.storeOption} onClick={() => handleValueSelect(val)}>
                              {multiSelect ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <CustomCheckbox
                                    checked={selectedValues.has(val)}
                                    onChange={() => handleValueSelect(val)}
                                    label={val}
                                  />
                                </div>
                              ) : (
                                <span style={styles.optionText} title={val}>
                                  {val}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  inputLabel: {
    fontSize: 14,
    color: "#333",
    fontWeight: 500,
    marginBottom: 8,
  },
  selectWrapper: {
    position: "relative",
  },
  selectInput: {
    padding: "12px 15px",
    border: "1px solid #ddd",
    borderRadius: 16,
    fontSize: 12,
    backgroundColor: "#fff",
    cursor: "pointer",
    height: 44,
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  selectText: {
    color: "#4A4A4A",
    fontWeight: 500,
  },
  selectArrow: {
    color: "#4A4A4A",
    transition: "transform 0.2s",
    fontSize: 12,
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
    maxHeight: 400,
    display: "flex",
    flexDirection: "column",
  },
  searchWrapper: {
    padding: 12,
    borderBottom: "1px solid #eee",
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 4,
    fontSize: 14,
    boxSizing: "border-box",
  },
  optionsList: {
    overflowY: "auto",
    maxHeight: 320,
  },
  option: {
    padding: "10px 15px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    borderBottom: "1px solid #f5f5f5",
    fontWeight: 500,
  },
  stateOption: {
    padding: "10px 15px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    backgroundColor: "#f9f9f9",
    borderBottom: "1px solid #eee",
    fontWeight: 500,
  },
  storeOption: {
    padding: "10px 15px 10px 50px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    borderBottom: "1px solid #f5f5f5",
  },
  storesList: {
    backgroundColor: "#fff",
    fontWeight: 500,
  },
  checkbox: {
    width: 16,
    height: 16,
    cursor: "pointer",
    color: "#E5B42F",
    border: "1px solid #E5B42F",
    outline: "none",
  },
  chevron: {
    fontSize: 12,
    color: "#E5B42F",
    transition: "transform 0.2s",
    width: 12,
  },
  optionText: {
    fontSize: 14,
    color: "#333",
  },
  selectAllRow: {
    padding: "10px 15px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
    backgroundColor: "#f1f1f1",
  },
};
export default ItemSelect;
